/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import { type TFunction } from 'react-i18next';

import _ from 'lodash';
import queryString from 'query-string';
import sortBy from 'lodash/sortBy';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import { connect } from 'react-redux';

import DataUtils from 'common/utils/DataUtils';
import {
    PaginatedTable,
    StickyPositionEnum,
    DEFAULT_PAGE_SIZE,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';

import DefaultActionsBar from 'common/entity/ActionsBar';
import DefaultBulkViewEmptyState from 'common/entity/BulkViewEmptyState';
import { Checkbox, CheckboxTypes } from 'common/components/core/checkbox';
import { ApiError } from 'common/modules/api';

import theme from 'common/entity/BulkEditContainer.scss';

import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';
import DocumentsColumnHelper from 'common/entity/helpers/DocumentsColumnHelper';
import { dashboardColors } from 'common/enums/ColorEnum';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { ConfirmationDialog } from 'common/components/core/dialog/ConfirmationDialog';

import type { RouterHistory } from 'react-router-dom';
import type { Location } from 'common/components/core/router';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { OrderManagementSettings } from 'common/models/internal/OrderManagementSettings';
import type { ToastType } from 'common/components/core/toast/Toast';
import EntityExportHandler from 'common/entity/generic/EntityExportHandler';
import type { EntityCellDataTypesEnumType } from 'common/components/core/table/CellDataTypes';
import QrCodeModal from './QrCodeModal';
import { getLocaleSettings } from 'common/redux/selectors/clientSelector';
import { type LocaleSettings } from 'common/models/internal/LocaleSettings';

import {
    PermissionMatch,
    AccessDeniedDialog,
    type RoleOperationEnumType,
    RoleOperationEnum
} from 'common/modules/rbac';
import { DateTimeFormat } from 'common/utils/TimeUtils';

import entityIds from 'common/tests/entity/entityIds.json';

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type EntityDataType = {
    entities: Array<Object>,
    paginationInfo: PaginationInfoDataType
};

export type QrCodeDataType = {
    qrCodeImage: string,
    validity: string,
    riderId: {
        riderId: string
    }
};

export type QrCodesType = Array<QrCodeDataType>;

type Props = {
    clientId: string,
    fetchEntities: ({ clientId: string }, Object) => void,
    requestExportEntities: (clientId: string, options: Object) => void,
    fetchExportStatus: Function,
    showToastAction: Function,
    updateToastAction: Function,
    hideToastAction: Function,
    fetchSkills: ({ clientId: string }) => void,
    disableEntity: ({ clientId: string, [key: string]: string }) => Promise<*>,
    updateEntity: ({
        params: Object,
        body: Object
    }) => Promise<*>,
    bulkDisableEntity: ({
        params: Object,
        body: Object
    }) => Promise<*>,
    title: string,
    entityNameId: string,
    entityExportId: ?string,
    emptyStateImageSrc: string,
    emptyStateTitle?: string,
    emptyStateSubtitle?: string,
    entitySettingsName: string,
    toggleVerification: (
        { clientId: string, locationId: string },
        Object,
        options?: Object
    ) => Promise<*>,
    clearData: () => void,
    data: ?EntityDataType,
    isAddEntityActive: boolean,
    isImportEntityActive: boolean,
    entityIdToEdit: ?string,
    entityId: string,
    isLoading: boolean,
    location: Location,
    history: RouterHistory,
    theme: Object,
    entitySettings: ?EntitySettings | OrderManagementSettings,
    selectedRows: Array<string>,
    filteringMode: boolean,
    showEmptyState: boolean,
    hideExportInMenu: boolean,
    onEntityExportDismiss: Function,
    fetchOptions: Object,
    children?: React.Element<any>,
    tableSettings: ?Array<Object>,
    customActions?: Object,
    customData?: Object,
    updateLocation: (
        {
            clientId: string,
            locationId: string
        },
        Object,
        Object,
        options?: Object
    ) => Promise<*>,
    showSuccessToast: ($Shape<ToastType>) => void,
    showErrorToast: ($Shape<ToastType>) => void,
    saveTableSettings: (string, Array<Object>) => void,
    getExtraColumnProperties?: () => Array<Object>,
    getExtraFiltersForFetchData?: (Array<Object>) => Array<Object>,
    customProcessColumns?: (Array<Object>) => Array<Object>,
    getTdProps: () => Object,
    setSelectedRows: (Array<string>) => void,
    setAllFilteredEntitiesSelected: boolean => void,
    onSelectAllFilteredEntities: () => void,
    setShowEmptyState: boolean => void,
    toggleSelectAll: () => void,
    getAdditionalTableProps?: () => Object,
    refProvider: (React.ElementRef<any>) => void,
    getIsRowLoading?: Object => boolean,
    frozenColumnLimit: number,
    updatePjpLimitStatus: Function,
    isFieldHidden: () => boolean,
    shouldHideAddEntity: boolean,
    shouldHideEditAndDeleteEntity: boolean,
    entityCanBeDisabled: ?boolean,
    shouldHideEditEntity: boolean,
    shouldHideDeleteEntity: boolean,
    enableResetPassword?: boolean | ((selectedRows: Array<string>) => boolean),
    enableResendPassword?: boolean,
    enableResetQrCode?: boolean,
    resetPassword?: Function,
    bulkResetPassword?: Function,
    shouldExportInactiveEntities?: boolean,
    hasAllSelected?: boolean, // to explicitly control header checkbox checked state
    hideSelectAll?: boolean, //to hide header select all checkbox
    disableEmptyStateAddButton?: boolean, //to hide add button in bulk view empty state,
    localeSettings: LocaleSettings,
    enableCascadeWarning?: boolean,
    customResourceLimitsColumns?: ?Array<EntityColumnType>,
    useLocalisation?: boolean
};

export type EntityColumnType = {
    id?: string,
    headerTextId?: string,
    accessor?: string | Array<string>,
    fieldId?: string,
    exportId?: string,
    headerText?: string | (() => React.Node),
    preferredUnitText?: string,
    property?: string,
    required?: boolean,
    Cell?: (props: $FixMe) => React.Node,
    translationProps?: ({ t: TFunction }) => { [key: string]: string },
    dataType?: EntityCellDataTypesEnumType,
    frozen?: boolean,
    disablePinControl?: boolean,
    sortable?: boolean,
    timeUnits?: Array<string>,

    // filter
    infixSearchable?: boolean,
    filterable?: boolean,
    FilterComponent?: React.AbstractComponent<any>,
    getFilterOptions?: Function,
    getCustomFilterInfo?: (params: {
        clientId: string,
        filteredColumn: { id: string, value: $FixMe }
    }) => void,

    // size
    width?: number,
    minWidth?: number,

    // visibility
    isHidden?: () => boolean,
    show?: boolean
};

type State = {
    columns: Array<EntityColumnType>,
    showBulkDisableConfirmation: ?boolean,
    showCascadeConfirmation: ?boolean,
    isEntityDisableInProgress: boolean,
    exportErrorMessage: ?string,
    pinnedColumns: Array<Object>,
    unpinnedColumns: Array<Object>,
    noPinCols: Array<Object>,
    frozenColumns: Array<Object>,
    showLoader?: boolean,
    isEnableEntityActive: boolean,
    showBulkResetPasswordConfirmation: boolean,
    isResetPasswordInProgress: boolean,
    showBulkResendPasswordConfirmation: boolean,
    isResendPasswordInProgress: boolean,
    showResetPasswordConfirmation: boolean,
    showResetQrCodeConfirmation: boolean,
    showGeneratedQrCode: boolean,
    qrCodes: QrCodesType
};
const ENTITY_EXPORT_HANDLER = new EntityExportHandler();

const getDateFormat = (localeSettings: LocaleSettings) => {
    const isDateFormatMMDDYYYY = localeSettings.isDateFormatMMDDYYYY();
    const dateFormat = isDateFormatMMDDYYYY
        ? DateTimeFormat.FORMAT_Do_MMM_YYYY_MONTH_FIRST
        : DateTimeFormat.FORMAT_Do_MMM_YYYY;
    return dateFormat;
};

const entityMasterFactory = ({
    ActionsBar = DefaultActionsBar,
    BulkViewEmptyState = DefaultBulkViewEmptyState,
    PaginatedTableComponent = PaginatedTable,
    EntityEditViewContainer,
    EntityImportContainer,
    EntityExportContainer,
    COLUMNS,
    EXPORT_HANDLER = ENTITY_EXPORT_HANDLER,
    options,
    EnableEntityView
}: {
    ActionsBar?: $FixMe,
    BulkViewEmptyState?: $FixMe,
    PaginatedTableComponent?: $FixMe,
    EntityEditViewContainer: $FixMe,
    EntityImportContainer: $FixMe,
    EntityExportContainer: $FixMe,
    COLUMNS: $FixMe,
    EXPORT_HANDLER?: $FixMe,
    options: {
        entityTableId: string,
        operations?: {
            edit?: RoleOperationEnumType,
            add?: RoleOperationEnumType,
            export?: RoleOperationEnumType
        }
    },
    EnableEntityView?: $FixMe
}): React.ComponentType<Props> => {
    const { entityTableId } = options;
    const operations = {
        edit: RoleOperationEnum.ALLOWED,
        export: RoleOperationEnum.ALLOWED,
        add: RoleOperationEnum.ALLOWED,
        ...options.operations
    };
    class EntityMasterContainer extends React.PureComponent<Props, State> {
        prevRequestParams: Object;

        static defaultProps = {
            filteringMode: true,
            hideExportInMenu: false,
            useLocalisation: true
        };

        constructor(props: Props) {
            super(props);
            this.state = {
                columns: [],
                pinnedColumns: [],
                unpinnedColumns: [],
                noPinCols: [],
                showBulkDisableConfirmation: false,
                showCascadeConfirmation: false,
                isEntityDisableInProgress: false,
                exportErrorMessage: null,
                frozenColumns: [],
                isEnableEntityActive: false,
                showBulkResetPasswordConfirmation: false,
                isResetPasswordInProgress: false,
                showBulkResendPasswordConfirmation: false,
                isResendPasswordInProgress: false,
                isResetQrCodeInProgress: false,
                showResetPasswordConfirmation: false,
                showResetQrCodeConfirmation: false,
                showGeneratedQrCode: false,
                qrCodes: []
            };
            this.onTableStateChange = debounce(this.onTableStateChange, 500, {
                leading: true,
                trailing: true
            });
        }
        // Using UNSAFE_componentWillMount instead of componentDidMount because of issues during first render
        UNSAFE_componentWillMount() {
            const { refProvider } = this.props;
            this.updateColumnsData(this.props);
            refProvider && refProvider(this);
        }

        componentDidUpdate(prevProps: Props) {
            const {
                data,
                clientId,
                isLoading,
                showEmptyState,
                entitySettings,
                tableSettings,
                unitConverter,
                setShowEmptyState,
                toggleSelectAll,
                t
            } = this.props;

            if (
                entitySettings !== prevProps.entitySettings ||
                unitConverter !== prevProps.unitConverter ||
                toggleSelectAll !== prevProps.toggleSelectAll ||
                t !== prevProps.t
            ) {
                this.updateColumnsData(this.props);
            }
            if (!prevProps.tableSettings && tableSettings) {
                this.setState(prevState => ({
                    columns: this.mergeColumnsWithPrefs(
                        this.props,
                        prevState.columns
                    )
                }));
            }
            if (clientId !== prevProps.clientId) {
                this.fetchBulkData();
            }
            if (data) {
                if (data.entities.length === 0 && !isLoading) {
                    if (this.prevRequestParams.filters.length === 0) {
                        /*
                            if not loading, no data,
                            & filtering is turned off,
                            show the empty state
                        */
                        !showEmptyState && setShowEmptyState(true);
                    }
                } else {
                    /* if loading, don't show empty state  */
                    if (showEmptyState) {
                        setShowEmptyState(false);
                    }
                }
            }
        }

        componentWillUnmount() {
            this.props.clearData();
        }

        withFreezableProps(prevColumns) {
            const { hasFreezableColumns } = this.props;
            if (!hasFreezableColumns) {
                return {
                    columns: prevColumns,
                    frozenColumns: [],
                    unpinnedColumns: [],
                    pinnedColumns: [],
                    noPinCols: []
                };
            }
            let columns = prevColumns.map(column => {
                const { id, accessor } = column;
                return {
                    ...column,
                    columnId: `${id || accessor}`,
                    fixed: column.frozen
                        ? column.fixedPosition || StickyPositionEnum.Left
                        : false
                };
            });

            const frozenColumns = [];

            columns.forEach(column => {
                const { frozen, disablePinControl, columnId } = column;
                if (frozen && !disablePinControl) {
                    frozenColumns.push(columnId);
                }
            });
            const noPinCols = columns.filter(
                column => column.disablePinControl
            );
            const pinnedColumns = columns.filter(
                column => column.frozen && !column.disablePinControl
            );
            const unpinnedColumns = columns.filter(column => !column.frozen);
            columns = noPinCols.concat(pinnedColumns, unpinnedColumns);
            return {
                columns,
                frozenColumns,
                unpinnedColumns,
                pinnedColumns,
                noPinCols
            };
        }

        getExtraColumnProperties = () => {
            const { getExtraColumnProperties, toggleSelectAll } = this.props;
            const isVisible = Object.prototype.hasOwnProperty.call(
                this.props,
                'hideSelectAll'
            )
                ? !this.props.hideSelectAll
                : true;

            return [
                {
                    id: 'selector',
                    visible: true,
                    headerText: () => {
                        return isVisible ? (
                            <Checkbox
                                checkboxType={CheckboxTypes.Solid}
                                checkboxColor={dashboardColors.darkSkyBlue.name}
                                theme={theme}
                                className={theme.selectionCell}
                                checked={this.hasSelectedAll()}
                                onChange={toggleSelectAll}
                            />
                        ) : (
                            <></>
                        );
                    }
                },
                ...(getExtraColumnProperties ? getExtraColumnProperties() : [])
            ];
        };

        filterOutHiddenColumns = columns => {
            return columns.filter(column => {
                if (_.isFunction(column.isHidden)) {
                    return !column.isHidden(this.props);
                }
                return true;
            });
        };

        processColumns = (props: Props) => {
            const {
                customProcessColumns,
                entitySettings,
                unitConverter,
                entitySettingsName,
                customResourceLimitsColumns
            } = props;
            const extraColumnProperties = this.getExtraColumnProperties();

            //Adding custom resource columns if passed from individual entity master containers
            let modifiedColumns = [
                ...COLUMNS,
                ...(customResourceLimitsColumns || [])
            ];
            if (customProcessColumns) {
                modifiedColumns = customProcessColumns(modifiedColumns);
            }
            modifiedColumns = DocumentsColumnHelper.processColumns(
                entitySettings,
                modifiedColumns,
                entitySettingsName
            );

            modifiedColumns = this.filterOutHiddenColumns(modifiedColumns);

            return CustomColumnHelpers.processColumns(
                entitySettings,
                modifiedColumns,
                entitySettingsName,
                extraColumnProperties,
                unitConverter,
                customResourceLimitsColumns
            );
        };

        updateColumnsData = (props: Props) => {
            const _columns = this.processColumns(props);
            const {
                columns,
                frozenColumns,
                unpinnedColumns,
                pinnedColumns,
                noPinCols
            } = this.withFreezableProps(
                this.mergeColumnsWithPrefs(props, _columns)
            );
            this.setState({
                columns,
                frozenColumns,
                unpinnedColumns,
                pinnedColumns,
                noPinCols
            });
        };

        onColumnFreeze = (id, frozen) => {
            const { frozenColumnLimit: providedFrozenColumnLimit } = this.props;
            const unfrozenCol = [];
            const frozenColumnLimit =
                providedFrozenColumnLimit !== undefined
                    ? providedFrozenColumnLimit
                    : FROZEN_COLUMN_LIMIT;
            this.setState(prev => {
                const {
                    columns: prevColumns,
                    frozenColumns: prevFrozenColumns,
                    unpinnedColumns: prevUnpinnedColumns,
                    pinnedColumns: prevPinnedColumns,
                    noPinCols: prevNoPinCols
                } = prev;
                let proceedToChange = false;
                let frozenColumns;

                if (prevFrozenColumns.length < frozenColumnLimit && frozen) {
                    proceedToChange = true;
                    frozenColumns = prevFrozenColumns.slice(0);
                    frozenColumns.push(id);
                } else if (!frozen) {
                    proceedToChange = true;
                    frozenColumns = prevFrozenColumns.filter(_id => _id !== id);
                }

                let columns;
                let unpinnedColumns;
                let pinnedColumns;
                let noPinCols;

                if (proceedToChange) {
                    columns = prevColumns.map(column => {
                        const { columnId, frozen: prevFrozen } = column;
                        const currFreezeState =
                            columnId === id ? frozen : prevFrozen;
                        const modifiedCol = {
                            ...column,
                            frozen: currFreezeState,
                            fixed: currFreezeState
                                ? column.fixedPosition ||
                                  StickyPositionEnum.Left
                                : false
                        };
                        if (columnId === id && !frozen) {
                            unfrozenCol.push(modifiedCol);
                        }
                        return modifiedCol;
                    });
                    pinnedColumns = sortBy(
                        columns.filter(
                            column => column.frozen && !column.disablePinControl
                        ),
                        column => frozenColumns.indexOf(column.columnId)
                    );
                    const unFrozencid =
                        unfrozenCol[0] && unfrozenCol[0].columnId;
                    unpinnedColumns = unfrozenCol.concat(
                        columns.filter(
                            column =>
                                column.columnId !== unFrozencid &&
                                !column.frozen
                        )
                    );
                    noPinCols = columns.filter(
                        column => column.disablePinControl
                    );
                    columns = noPinCols.concat(pinnedColumns, unpinnedColumns);
                } else {
                    columns = [...prevColumns];
                    unpinnedColumns = [...prevUnpinnedColumns];
                    pinnedColumns = [...prevPinnedColumns];
                    noPinCols = [...prevNoPinCols];
                }

                const localPrefsData = columns.map(column => {
                    return {
                        id: column.id || column.accessor,
                        show: column.show,
                        frozen: column.frozen,
                        fixedPosition: column.fixedPosition
                    };
                });
                this.props.saveTableSettings(entityTableId, localPrefsData);

                return {
                    columns,
                    frozenColumns,
                    unpinnedColumns,
                    pinnedColumns,
                    noPinCols
                };
            });
        };

        onColumnsChange = (info: Object) => {
            const { columns, pinnedColumns, unpinnedColumns, noPinCols } = info;

            const newState = {};
            newState.columns = columns;
            pinnedColumns && (newState.pinnedColumns = pinnedColumns);
            unpinnedColumns && (newState.unpinnedColumns = unpinnedColumns);
            noPinCols && (newState.noPinCols = noPinCols);

            this.setState(newState);

            const localPrefsData = columns.map(column => {
                return {
                    id: column.id || column.accessor,
                    show: column.show,
                    frozen: column.frozen,
                    fixedPosition: column.fixedPosition
                };
            });
            this.props.saveTableSettings(entityTableId, localPrefsData);
        };

        mergeColumnsWithPrefs = (props: Props, columns: Array<Object>) => {
            const { tableSettings } = props;

            if (!tableSettings) {
                // setting show to true, so that initially when nothing is in tableSettings
                // changing order of columns doesn't break table
                return columns.map(column => {
                    if (column.accessor === 'id' && column.show === false) {
                        return { ...column };
                    }
                    return {
                        ...column,
                        show: DataUtils.isBoolean(column.show)
                            ? column.show
                            : true
                    };
                });
            }
            const visibilitySyncedColumns = columns.map(column => {
                const id = column.id || column.accessor;
                const _show = DataUtils.isBoolean(column.show)
                    ? column.show
                    : true;
                const matchingSavedColumn = tableSettings.find(
                    savedColumn => savedColumn.id === id
                );
                if (!matchingSavedColumn) {
                    return {
                        ...column,
                        show: _show // new column if show is not there default true
                    };
                }
                return {
                    ...column,
                    frozen:
                        'frozen' in matchingSavedColumn
                            ? matchingSavedColumn.frozen
                            : column.frozen,
                    fixedPosition:
                        'fixedPosition' in matchingSavedColumn
                            ? matchingSavedColumn.fixedPosition
                            : column.fixedPosition,
                    show: !('show' in matchingSavedColumn)
                        ? _show
                        : matchingSavedColumn.show
                };
            });
            const savedSortedIds = tableSettings.map(column => column.id);
            return sortBy(visibilitySyncedColumns, column => {
                const id = column.id || column.accessor;
                const sortedIndexInSavedSettings = savedSortedIds.indexOf(id);
                if (sortedIndexInSavedSettings === -1) {
                    // new column case
                    // keeping same order kept in config
                    return visibilitySyncedColumns.findIndex(
                        ({ id }) => id === column.id
                    );
                }
                return sortedIndexInSavedSettings;
            });
        };

        resetSelectedRows = () => {
            const {
                setSelectedRows,
                setAllFilteredEntitiesSelected
            } = this.props;
            setAllFilteredEntitiesSelected(false);
            setSelectedRows([]);
        };

        fetchBulkData = (
            page: ?number = 1,
            pageSize: ?number = DEFAULT_PAGE_SIZE,
            sortingInfo: ?Array<Object>,
            filters: Array<Object> = []
        ) => {
            const {
                fetchEntities,
                getExtraFiltersForFetchData,
                fetchOptions,
                fetchBulkData,
                fetchEntityPollingHandler
            } = this.props;
            this.resetSelectedRows();
            let updatedFilters = new FilterListBuilder().addAll(filters);
            if (getExtraFiltersForFetchData) {
                const extraFilters = getExtraFiltersForFetchData(filters);
                updatedFilters = updatedFilters.addAll(extraFilters);
            }

            this.prevRequestParams = {
                page,
                size: pageSize,
                sortingInfo,
                filters,
                allFilters: updatedFilters
            };
            fetchEntities(
                fetchOptions,
                {
                    page,
                    size: pageSize,
                    sortingInfo,
                    filters: updatedFilters.build()
                },
                { onSuccess: fetchEntityPollingHandler }
            );
            fetchBulkData && fetchBulkData();
        };

        reloadData = () => {
            const { page, size, sortingInfo, filters } = this.prevRequestParams;
            this.fetchBulkData(page, size, sortingInfo, filters);
        };

        handleEditEntityClick = () => {
            const { selectedRows, location, history } = this.props;
            const modifiedQueries = _.extend(
                { edit: selectedRows[0] },
                location.query
            );
            history.push({
                ...location,
                search: queryString.stringify(modifiedQueries)
            });
        };

        onDeleteClick = () => {
            const { selectedRows, enableCascadeWarning } = this.props;
            if (selectedRows && selectedRows.length <= 1) {
                if (enableCascadeWarning) {
                    return this.setState({
                        showCascadeConfirmation: true
                    });
                }
                this.handleDeleteEntityClick();
            } else {
                this.setState({
                    showBulkDisableConfirmation: true
                });
            }
        };

        onBulkDeleteDismiss = () => {
            this.setState({
                showBulkDisableConfirmation: false,
                isEntityDisableInProgress: false
            });
        };

        onCascadeWarningDismiss = () => {
            this.setState({
                showCascadeConfirmation: false,
                isEntityDisableInProgress: false
            });
        };

        onResetClick = (): void => {
            const { selectedRows } = this.props;
            if (selectedRows?.length <= 1) {
                this.setState({
                    showResetPasswordConfirmation: true
                });
            } else {
                this.setState({
                    showBulkResetPasswordConfirmation: true
                });
            }
        };

        onBulkResetPasswordDismiss = (): void => {
            this.setState({
                showBulkResetPasswordConfirmation: false,
                isResetPasswordInProgress: false
            });
        };

        onResetPasswordDismiss = (): void => {
            this.setState({
                showResetPasswordConfirmation: false,
                isResetPasswordInProgress: false
            });
        };

        onResetQrCodeClick = (): void => {
            this.setState({
                showResetQrCodeConfirmation: true
            });
        };

        onResetQrCodeDismiss = (): void => {
            this.setState({
                showResetQrCodeConfirmation: false,
                isResetQrCodeInProgress: false
            });
            this.resetSelectedRows();
        };

        onResendClick = (): void => {
            const { selectedRows } = this.props;
            if (selectedRows?.length <= 1) {
                this.handleResetPasswordClick();
            } else {
                this.setState({
                    showBulkResendPasswordConfirmation: true
                });
            }
        };

        onBulkResendPasswordDismiss = (): void => {
            this.setState({
                showBulkResendPasswordConfirmation: false,
                isResendPasswordInProgress: false
            });
        };

        handleBulkDeleteEntities = () => {
            const { isEntityDisableInProgress } = this.state;
            if (isEntityDisableInProgress) {
                return;
            }
            const {
                bulkDisableEntity,
                selectedRows,
                clientId,
                onDeleteSuccess,
                onDeleteFailure,
                allFilteredEntitiesSelected,
                getExtraFiltersForFetchData,
                entityCanBeDisabled
            } = this.props;
            const { filters } = this.prevRequestParams;
            const extraFilters = getExtraFiltersForFetchData
                ? getExtraFiltersForFetchData(filters)
                : [];
            let body;
            const statusFilter = new FilterBuilder()
                .withName('status')
                .withOperation('EQUALS')
                .withValue('ACTIVE');

            if (allFilteredEntitiesSelected) {
                const updatedFilters = [
                    ...filters,
                    ...extraFilters,
                    statusFilter
                ];
                body = {
                    filters: new FilterListBuilder()
                        .addAll(updatedFilters)
                        .build()
                };
            } else {
                body = {
                    selectedIds: selectedRows
                };
            }
            if (selectedRows.length) {
                this.setState({
                    isEntityDisableInProgress: true
                });
                bulkDisableEntity({
                    params: { clientId },
                    body,
                    options: { entityCanBeDisabled }
                })
                    .then(() => {
                        this.setState({
                            showBulkDisableConfirmation: false,
                            isEntityDisableInProgress: false
                        });
                        this.reloadData();
                        this.resetSelectedRows();
                        onDeleteSuccess && onDeleteSuccess();
                    })
                    .catch(error => onDeleteFailure && onDeleteFailure(error))
                    .finally(() =>
                        this.setState({
                            isEntityDisableInProgress: false
                        })
                    );
            }
        };

        bulkResetQrCodeApiCall = (callback): void => {
            const {
                bulkResetQrCode,
                selectedRows,
                clientId,
                allFilteredEntitiesSelected,
                getExtraFiltersForFetchData
            } = this.props;
            const { filters } = this.prevRequestParams;
            const extraFilters = getExtraFiltersForFetchData?.(filters) || [];

            this.setState({
                isResetQrCodeInProgress: true
            });

            let body;
            const statusFilter = new FilterBuilder()
                .withName('status')
                .withOperation('EQUALS')
                .withValue('ACTIVE');

            if (allFilteredEntitiesSelected) {
                const updatedFilters = [
                    ...filters,
                    ...extraFilters,
                    statusFilter
                ];
                body = {
                    filters: new FilterListBuilder()
                        .addAll(updatedFilters)
                        .build()
                };
            } else {
                body = {
                    selectedIds: selectedRows
                };
            }
            if (selectedRows.length) {
                bulkResetQrCode({
                    params: { clientId },
                    body
                }).then(response => {
                    if (response instanceof ApiError) {
                        this.setState({
                            isResetQrCodeInProgress: false
                        });
                        return;
                    }
                    this.resetSelectedRows();
                    callback(response);
                });
            }
        };

        handleBulkResendPassword = ({
            isReset = false
        }: {
            isReset: boolean
        }): void => {
            const {
                bulkResetPassword,
                selectedRows,
                clientId,
                allFilteredEntitiesSelected,
                getExtraFiltersForFetchData
            } = this.props;
            const { filters } = this.prevRequestParams;
            const extraFilters = getExtraFiltersForFetchData?.(filters) || [];
            if (isReset) {
                this.setState({
                    isResetPasswordInProgress: true
                });
            } else {
                this.setState({
                    isResendPasswordInProgress: true
                });
            }
            let body;
            const statusFilter = new FilterBuilder()
                .withName('status')
                .withOperation('EQUALS')
                .withValue('ACTIVE');

            if (allFilteredEntitiesSelected) {
                const updatedFilters = [
                    ...filters,
                    ...extraFilters,
                    statusFilter
                ];
                body = {
                    filters: new FilterListBuilder()
                        .addAll(updatedFilters)
                        .build()
                };
            } else {
                body = {
                    selectedIds: selectedRows
                };
            }
            if (selectedRows.length) {
                bulkResetPassword({
                    params: { clientId, isReset },
                    body
                })
                    .then(() => {
                        this.reloadData();
                        this.resetSelectedRows();
                    })
                    .finally(() => {
                        if (isReset) {
                            this.onBulkResetPasswordDismiss();
                        } else {
                            this.onBulkResendPasswordDismiss();
                        }
                    });
            }
        };

        handleBulkResetPassword = (): void =>
            this.handleBulkResendPassword({ isReset: true });

        handleDeleteEntityClick = () => {
            const {
                clientId,
                selectedRows,
                entityId,
                disableEntity,
                onDeleteSuccess,
                onDeleteFailure
            } = this.props;
            const [firstEntityId] = selectedRows;
            if (!firstEntityId) {
                return;
            }
            this.setState({
                isEntityDisableInProgress: true
            });
            disableEntity({
                clientId,
                [entityId]: firstEntityId
            })
                .then(() => {
                    this.reloadData();
                    this.resetSelectedRows();
                    onDeleteSuccess && onDeleteSuccess();
                })
                .catch(error => onDeleteFailure && onDeleteFailure(error))
                .finally(() =>
                    this.setState({
                        isEntityDisableInProgress: false,
                        showCascadeConfirmation: false
                    })
                );
        };

        handleResetPasswordConfirmation = () => {
            this.setState({ isResetPasswordInProgress: true });
            this.resetPasswordApiCall(true, this.onResetPasswordDismiss);
        };

        handleResetQrCodeConfirmation = () => {
            this.setState({
                isResetQrCodeInProgress: true
            });
            this.bulkResetQrCodeApiCall(this.onResetQrCodeSuccess);
        };

        onResetQrCodeSuccess = apiResponse => {
            this.setState({
                isResetQrCodeInProgress: false,
                showResetQrCodeConfirmation: false,
                showGeneratedQrCode: true,
                qrCodes: apiResponse?.qrCodes || []
            });
        };

        resetPasswordApiCall = (
            isReset: boolean,
            callback?: Function
        ): void => {
            const {
                clientId,
                selectedRows,
                entityId,
                resetPassword
            } = this.props;
            const [firstEntityId] = selectedRows;
            resetPassword?.({
                clientId,
                [entityId]: firstEntityId,
                isReset
            }).then(() => {
                this.reloadData();
                this.resetSelectedRows();
                if (isReset) {
                    this.setState({ isResetPasswordInProgress: false });
                } else {
                    this.setState({ isResendPasswordInProgress: false });
                }
                callback?.();
            });
        };

        handleResetPasswordClick = (isReset: boolean = false): void => {
            const { selectedRows } = this.props;
            const [firstEntityId] = selectedRows;
            if (!firstEntityId) {
                return;
            }
            if (isReset) {
                this.setState({
                    isResetPasswordInProgress: true,
                    showResetPasswordConfirmation: true
                });
            } else {
                this.setState({ isResendPasswordInProgress: true });
                this.resetPasswordApiCall(isReset);
            }
        };

        handleAddEntityClick = () => {
            const { location, history } = this.props;
            const modifiedQueries = _.extend({ add: null }, location.query);
            history.push({
                ...location,
                search: queryString.stringify(modifiedQueries)
            });
        };

        handleSingleEntityEditDismiss = (changed: boolean = false) => {
            const { location, history } = this.props;
            const clonedQueries = _.extend({}, location.query);
            delete clonedQueries.add;
            delete clonedQueries.edit;
            history.push({
                ...location,
                search: queryString.stringify(clonedQueries)
            });
            if (changed) {
                this.reloadData();
            }
        };

        handleImportEntityClick = () => {
            const { location, history } = this.props;
            const modifiedQueries = _.extend({ import: null }, location.query);
            history.push({
                ...location,
                search: queryString.stringify(modifiedQueries)
            });
        };

        handleEntityImportDismiss = (changed: boolean = false) => {
            const { location, history } = this.props;
            const clonedQueries = _.extend({}, location.query);
            delete clonedQueries.import;
            history.push({
                ...location,
                search: queryString.stringify(clonedQueries)
            });
            if (changed) {
                this.reloadData();
            }
        };

        handleExportEntityClick = () => {
            const { location, history } = this.props;
            const modifiedQueries = _.extend(
                { entityExport: null },
                location.query
            );
            history.push({
                ...location,
                search: queryString.stringify(modifiedQueries)
            });
            this.resetExportErrorMessage();
        };

        resetExportErrorMessage = () => {
            this.setState({
                exportErrorMessage: null,
                showLoader: false
            });
        };

        handleEntityExportDismiss = (changed: boolean = false) => {
            const { location, history, onEntityExportDismiss } = this.props;
            const clonedQueries = _.extend({}, location.query);
            delete clonedQueries.entityExport;
            history.push({
                ...location,
                search: queryString.stringify(clonedQueries)
            });
            onEntityExportDismiss && onEntityExportDismiss();
            if (changed) {
                this.reloadData();
            }
            this.setState({ showLoader: false });
        };

        handleEntityExportAccept = (requestParams: Object) => {
            const {
                clientId,
                showToastAction,
                updateToastAction,
                hideToastAction,
                getAdditionalExportOptions,
                t,
                entityExportId
            } = this.props;
            this.setState({ showLoader: true });
            let options = getAdditionalExportOptions
                ? getAdditionalExportOptions()
                : {};

            options = {
                entityMasterName: t(`entityExport.master.${entityExportId}`),
                ...options
            };
            EXPORT_HANDLER.initialiseAndMakeExportRequest(
                requestParams,
                clientId,
                showToastAction,
                updateToastAction,
                hideToastAction,
                options
            )
                .then(() => {
                    this.handleEntityExportDismiss();
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        const { message } = error;
                        this.setState({
                            exportErrorMessage: message
                        });
                    }
                    this.setState({
                        showLoader: false
                    });
                });
        };

        getTdProps = (state, rowInfo) => {
            const { getTdProps } = this.props;
            let tdProps;
            if (getTdProps) {
                tdProps = {
                    ...getTdProps(state, rowInfo),
                    resetExportErrorMessage: this.resetExportErrorMessage,
                    partialUpdateEntity: this.handlePartialEntityUpdate,
                    reloadData: this.reloadData
                };
            }
            return tdProps || {};
        };

        onTableStateChange = ({
            pageSize,
            page,
            sorted,
            filtered
        }: {
            pageSize: number,
            page: number,
            sorted: Array<Object>,
            filtered: Array<Object>
        }) => {
            const { clientId } = this.props;
            const { columns } = this.state;
            const sortingInfo = sorted.map(sortedColumn => {
                const column = columns.find(column => {
                    return (
                        column.id === sortedColumn.id ||
                        column.accessor === sortedColumn.id
                    );
                });
                if (!column) {
                    throw new Error(`column not found ${sortedColumn.id}`);
                }
                return {
                    name: column.property || column.accessor,
                    order: sortedColumn.desc ? 'DESC' : 'ASC'
                };
            });
            const filterInfo = filtered
                .filter(filteredColumn => !isEmpty(filteredColumn.value))
                .map(filteredColumn => {
                    const column = this.state.columns.find(column => {
                        return (
                            column.id === filteredColumn.id ||
                            column.accessor === filteredColumn.id
                        );
                    });
                    if (!column) {
                        throw new Error(
                            `column not found ${filteredColumn.id}`
                        );
                    }

                    if (column.getCustomFilterInfo) {
                        const customFilterInfo = column.getCustomFilterInfo({
                            clientId,
                            filteredColumn
                        });
                        if (customFilterInfo) {
                            return customFilterInfo;
                        }
                    }

                    return new FilterBuilder()
                        .withName(column.property || column.accessor)
                        .withOperation(
                            column.infixSearchable
                                ? FilterOperation.ContainsToken
                                : FilterOperation.StartsWith
                        )
                        .withValue(filteredColumn.value);
                });
            const allFilters = _.flatten(filterInfo);
            this.fetchBulkData(page + 1, pageSize, sortingInfo, allFilters);
        };

        hasSelectedAll() {
            const { data, selectedRows } = this.props;
            if (!data) {
                return false;
            }
            if (
                Object.prototype.hasOwnProperty.call(
                    this.props,
                    'hasAllSelected'
                )
            )
                return this.props.hasAllSelected;

            const { entities } = data;
            return (
                selectedRows.length > 0 &&
                selectedRows.length === entities.length
            );
        }

        handlePartialEntityUpdate = (original, value, accessor) => {
            const { updateEntity, clientId, entityId } = this.props;
            const updatedEntity = original.setIn(accessor.split('.'), value);
            const params = { clientId, [entityId]: original.get('id') };
            return (
                updateEntity &&
                updateEntity(params, original, updatedEntity).then(() => {
                    this.reloadData();
                })
            );
        };

        renderEmptyState() {
            const {
                children,
                isAddEntityActive,
                entityIdToEdit,
                emptyStateImageSrc,
                emptyStateTitle,
                emptyStateSubtitle,
                title,
                isFieldHidden,
                entityNameId,
                theme,
                isImportEntityActive,
                customActions,
                customData,
                entitySettings,
                t,
                disableEmptyStateAddButton = false
            } = this.props;
            return (
                <div className={theme.bulkEditContainer}>
                    {ActionsBar ? (
                        <ActionsBar
                            {...(customActions ? customActions : {})}
                            {...(customData ? customData : {})}
                            isEmptyState
                            disableColumnModifier
                            title={title}
                            entityNameId={entityNameId}
                            operations={operations}
                        />
                    ) : null}
                    <BulkViewEmptyState
                        imageSrc={emptyStateImageSrc}
                        title={emptyStateTitle}
                        subtitle={emptyStateSubtitle}
                        entityNameId={entityNameId}
                        onAddClick={this.handleAddEntityClick}
                        onImportClick={
                            EntityImportContainer &&
                            this.handleImportEntityClick
                        }
                        disableAddButton={disableEmptyStateAddButton}
                        operations={operations}
                    />
                    {(isAddEntityActive || entityIdToEdit) &&
                    EntityEditViewContainer ? (
                        <PermissionMatch
                            operations={
                                isAddEntityActive
                                    ? operations.add
                                    : operations.edit
                            }
                            render={hasPermission => {
                                return hasPermission ? (
                                    <EntityEditViewContainer
                                        entityId={entityIdToEdit}
                                        entitySettings={entitySettings}
                                        isFieldHidden={isFieldHidden}
                                        onDismiss={
                                            this.handleSingleEntityEditDismiss
                                        }
                                        reloadData={this.reloadData}
                                    />
                                ) : (
                                    <AccessDeniedDialog
                                        action={t(
                                            isAddEntityActive ? 'add' : 'edit'
                                        )}
                                        onDismiss={
                                            this.handleSingleEntityEditDismiss
                                        }
                                    />
                                );
                            }}
                        />
                    ) : (
                        []
                    )}
                    {isImportEntityActive && EntityImportContainer ? (
                        <PermissionMatch
                            operations={operations.add}
                            render={hasPermission => {
                                return hasPermission ? (
                                    <EntityImportContainer
                                        isFieldHidden={isFieldHidden}
                                        entitySettings={entitySettings}
                                        onDismiss={
                                            this.handleEntityImportDismiss
                                        }
                                        clientId={this.props.clientId}
                                    />
                                ) : (
                                    <AccessDeniedDialog
                                        action={t(
                                            'entityManagement:importEntity',
                                            { entityNameId }
                                        )}
                                        onDismiss={
                                            this.handleEntityImportDismiss
                                        }
                                    />
                                );
                            }}
                        />
                    ) : (
                        []
                    )}
                    {isFunction(children)
                        ? children({ onDelete: this.handleDeleteEntityClick })
                        : children}
                </div>
            );
        }

        toggleEnableEntityView = () => {
            this.setState(prevState => ({
                isEnableEntityActive: !prevState.isEnableEntityActive
            }));
        };

        getQrCodeInnerText = () => {
            const {
                selectedRows,
                data,
                allFilteredEntitiesSelected,
                t
            } = this.props;
            const totalPageEntities = data?.paginationInfo?.total;
            const displaySelectedRows = !allFilteredEntitiesSelected
                ? selectedRows.length
                : totalPageEntities;
            return t(
                `entityManagement:${
                    displaySelectedRows > 1 ? 'bulkR' : 'r'
                }esetQrCodeConfirmationMessage`,
                {
                    selectedRows: displaySelectedRows
                }
            );
        };

        onGeneratedQrCodeDismiss = () => {
            this.setState({
                showGeneratedQrCode: false
            });
        };

        render() {
            const {
                theme,
                isLoading,
                isPolling,
                data,
                isAddEntityActive,
                filteringMode,
                selectedRows,
                showEmptyState,
                entityIdToEdit,
                entityNameId,
                entityExportId,
                title,
                children,
                getAdditionalTableProps,
                customActions,
                customData,
                isImportEntityActive,
                getIsRowLoading,
                hideExportInMenu,
                hideExportOptions,
                hideSubtitle,
                isExportEntityActive,
                SubComponent,
                expanded,
                onExpandedChange,
                onSortedChange,
                onFilteredChange,
                onPageChange,
                onPageSizeChange,
                enableFilteredEntitiesSelection,
                allFilteredEntitiesSelected,
                onSelectAllFilteredEntities,
                collapseOnDataChange,
                getExtraFiltersForFetchData,
                getAdditionalColumns,
                hasFreezableColumns,
                frozenColumnLimit,
                pinTooltip,
                unpinTooltip,
                defaultSorted,
                bulkDisableEntity,
                disableAddWithText,
                t,
                isFieldHidden,
                updatePjpLimitStatus,
                entitySettings,
                shouldHideAddEntity,
                disableCheckbox,
                entityCanBeDisabled,
                shouldHideEditEntity,
                shouldHideDeleteEntity,
                enableResetPassword,
                enableResendPassword,
                enableResetQrCode,
                shouldExportInactiveEntities,
                localeSettings,
                enableCascadeWarning,
                useLocalisation
            } = this.props;

            const {
                columns,
                exportErrorMessage,
                frozenColumns,
                pinnedColumns,
                unpinnedColumns,
                noPinCols,
                showLoader,
                isEntityDisableInProgress,
                showBulkDisableConfirmation,
                showCascadeConfirmation,
                isEnableEntityActive,
                showBulkResetPasswordConfirmation,
                isResetPasswordInProgress,
                showBulkResendPasswordConfirmation,
                isResendPasswordInProgress,
                isResetQrCodeInProgress,
                showResetPasswordConfirmation,
                showResetQrCodeConfirmation,
                showGeneratedQrCode,
                qrCodes
            } = this.state;

            let paginationInfo = null;
            if (data) {
                ({ paginationInfo } = data);
            }
            let enableEdit = false;
            let enableDelete = false;
            if (!shouldHideEditEntity && selectedRows.length === 1) {
                enableEdit = true;
            }
            if (!shouldHideDeleteEntity && selectedRows.length === 1) {
                enableDelete = true;
            }
            const isBulkDeleteEnabled = bulkDisableEntity ? true : false;
            const isResetPasswordEnabled =
                typeof enableResetPassword === 'boolean'
                    ? enableResetPassword
                    : enableResetPassword?.(selectedRows);
            const totalPageEntities = data?.paginationInfo?.total;
            if (showEmptyState) {
                // If filtering was enabled, some empty results can cause this, so dont show empty state if
                // filtering is enabled
                return this.renderEmptyState();
            }
            const selectedRowsCount = !allFilteredEntitiesSelected
                ? selectedRows.length
                : totalPageEntities;
            const entityName = t(`entities:${entityNameId}`, {
                count: selectedRowsCount
            });
            const bulkDisableWarningMessage = enableCascadeWarning
                ? t('entityManagement:entityCascadeDeleteConfirmationMessage', {
                      selectedRows: selectedRows.length
                  })
                : t('entityManagement:bulkDeleteConfirmationMessage', {
                      selectedRows: !allFilteredEntitiesSelected
                          ? selectedRows.length
                          : totalPageEntities
                  });
            return (
                <div className={theme.bulkEditContainer}>
                    {ActionsBar ? (
                        <ActionsBar
                            {...(customActions ? customActions : {})}
                            {...(customData ? customData : {})}
                            resetSelectedRows={this.resetSelectedRows}
                            bulkDisableEntity={bulkDisableEntity}
                            enableFilteredEntitiesSelection={
                                enableFilteredEntitiesSelection
                            }
                            isBulkDeleteEnabled={isBulkDeleteEnabled}
                            onSelectAllFilteredEntities={
                                onSelectAllFilteredEntities
                            }
                            allFilteredEntitiesSelected={
                                allFilteredEntitiesSelected
                            }
                            title={title}
                            entityNameId={entityNameId}
                            selectedRows={selectedRows}
                            isLoading={isLoading && !isPolling}
                            allApplicableFilters={
                                this.prevRequestParams &&
                                this.prevRequestParams.allFilters
                            }
                            filteringEnabled={
                                this.prevRequestParams &&
                                this.prevRequestParams.filters.length > 0
                            }
                            pageSize={
                                (this.prevRequestParams &&
                                    this.prevRequestParams.size) ||
                                DEFAULT_PAGE_SIZE
                            }
                            onAdd={
                                !shouldHideAddEntity && //Adding this condition to remove add contract button
                                this.handleAddEntityClick
                            }
                            onImport={
                                EntityImportContainer &&
                                this.handleImportEntityClick
                            }
                            onExport={
                                EntityExportContainer &&
                                !hideExportInMenu &&
                                this.handleExportEntityClick
                            }
                            onEdit={
                                enableEdit ? this.handleEditEntityClick : null
                            }
                            onDelete={
                                enableDelete || isBulkDeleteEnabled
                                    ? this.onDeleteClick
                                    : null
                            }
                            onResetPassword={
                                isResetPasswordEnabled
                                    ? this.onResetClick
                                    : null
                            }
                            onResendPassword={
                                enableResendPassword && this.onResendClick
                            }
                            onResetQrCode={
                                enableResetQrCode && this.onResetQrCodeClick
                            }
                            filteredEntriesCount={
                                data && data.paginationInfo.total
                            }
                            columns={columns}
                            onColumnsChange={this.onColumnsChange}
                            hasFreezableColumns={hasFreezableColumns}
                            unpinnedColumns={unpinnedColumns}
                            pinnedColumns={pinnedColumns}
                            noPinCols={noPinCols}
                            disableAddWithText={disableAddWithText}
                            onEnableEntity={
                                EnableEntityView
                                    ? this.toggleEnableEntityView
                                    : null
                            }
                            entityCanBeDisabled={entityCanBeDisabled}
                            isEntityDisableInProgress={
                                isEntityDisableInProgress
                            }
                            isResetPasswordInProgress={
                                isResetPasswordInProgress
                            }
                            isResendPasswordInProgress={
                                isResendPasswordInProgress
                            }
                            operations={operations}
                        />
                    ) : null}
                    <div className={theme.tableContainer}>
                        <PaginatedTableComponent
                            collapseOnDataChange={collapseOnDataChange}
                            {...(getAdditionalTableProps
                                ? getAdditionalTableProps()
                                : {})} //Props to force rerender
                            loading={isLoading && !isPolling}
                            enableFiltering={filteringMode}
                            data={data ? data.entities : []}
                            totalPages={
                                paginationInfo
                                    ? paginationInfo.numberOfPages
                                    : null
                            }
                            reloadData={this.reloadData}
                            getIsRowLoading={getIsRowLoading}
                            selectedRows={selectedRows} // used to render menu for bulk edit
                            columns={columns}
                            getTdProps={this.getTdProps}
                            onTableStateChange={this.onTableStateChange}
                            hasCheckbox={!disableCheckbox}
                            SubComponent={SubComponent}
                            expanded={expanded}
                            onExpandedChange={onExpandedChange}
                            onSortedChange={onSortedChange}
                            onFilteredChange={onFilteredChange}
                            onPageChange={onPageChange}
                            onPageSizeChange={onPageSizeChange}
                            theme={theme}
                            hasFreezableColumns={hasFreezableColumns}
                            freezeInfo={frozenColumns || []}
                            frozenColumnLimit={
                                frozenColumnLimit || FROZEN_COLUMN_LIMIT
                            }
                            onColumnFreeze={this.onColumnFreeze}
                            pinTooltip={pinTooltip}
                            unpinTooltip={unpinTooltip}
                            defaultSorted={defaultSorted}
                            handlePartialEntityUpdate={
                                this.handlePartialEntityUpdate
                            }
                            defaultDateFormat={getDateFormat(localeSettings)}
                            localeSettings={localeSettings}
                            useLocalisation={useLocalisation}
                        />
                    </div>
                    {(isAddEntityActive || entityIdToEdit) &&
                    EntityEditViewContainer ? (
                        <PermissionMatch
                            operations={
                                isAddEntityActive
                                    ? operations.add
                                    : operations.edit
                            }
                            render={hasPermission => {
                                return hasPermission ? (
                                    <EntityEditViewContainer
                                        entityId={entityIdToEdit}
                                        entitySettings={entitySettings}
                                        reloadData={this.reloadData}
                                        onDismiss={
                                            this.handleSingleEntityEditDismiss
                                        }
                                        isFieldHidden={isFieldHidden}
                                        updatePjpLimitStatus={
                                            updatePjpLimitStatus
                                        }
                                    />
                                ) : (
                                    <AccessDeniedDialog
                                        action={t(
                                            isAddEntityActive ? 'add' : 'edit'
                                        )}
                                        onDismiss={
                                            this.handleSingleEntityEditDismiss
                                        }
                                    />
                                );
                            }}
                        />
                    ) : (
                        []
                    )}
                    {isImportEntityActive && EntityImportContainer ? (
                        <PermissionMatch
                            operations={operations.add}
                            render={hasPermission => {
                                return hasPermission ? (
                                    <EntityImportContainer
                                        isFieldHidden={isFieldHidden}
                                        onDismiss={
                                            this.handleEntityImportDismiss
                                        }
                                        entitySettings={entitySettings}
                                        localeSettings={localeSettings}
                                        clientId={this.props.clientId}
                                    />
                                ) : (
                                    <AccessDeniedDialog
                                        action={t('import')}
                                        onDismiss={
                                            this.handleEntityImportDismiss
                                        }
                                    />
                                );
                            }}
                        />
                    ) : (
                        []
                    )}

                    {isExportEntityActive && EntityExportContainer ? (
                        <PermissionMatch
                            operations={operations.export}
                            render={hasPermission => {
                                return hasPermission ? (
                                    <EntityExportContainer
                                        shouldExportInactiveEntities={
                                            shouldExportInactiveEntities
                                        }
                                        showLoader={showLoader}
                                        entityNameId={entityExportId}
                                        columns={columns}
                                        selectedRows={selectedRows}
                                        getExtraFiltersForFetchData={
                                            getExtraFiltersForFetchData
                                        }
                                        getAdditionalColumns={
                                            getAdditionalColumns
                                        }
                                        appliedFilters={
                                            this.prevRequestParams &&
                                            this.prevRequestParams.allFilters
                                        }
                                        filteredEntriesCount={
                                            data && data.paginationInfo.total
                                        }
                                        filteringEnabled={
                                            this.prevRequestParams &&
                                            this.prevRequestParams.filters
                                                .length > 0
                                        }
                                        exportErrorMessage={exportErrorMessage}
                                        resetExportErrorMessage={
                                            this.resetExportErrorMessage
                                        }
                                        onDismiss={
                                            this.handleEntityExportDismiss
                                        }
                                        onAccept={this.handleEntityExportAccept}
                                        setExportStatus={this.setExportStatus}
                                        onError={this.onExportError}
                                        hideExportOptions={hideExportOptions}
                                        hideSubtitle={hideSubtitle}
                                    />
                                ) : (
                                    <AccessDeniedDialog
                                        action={t('export')}
                                        onDismiss={
                                            this.handleEntityExportDismiss
                                        }
                                    />
                                );
                            }}
                        />
                    ) : (
                        []
                    )}

                    {showBulkDisableConfirmation ? (
                        <ConfirmationDialog
                            isLoading={isEntityDisableInProgress}
                            theme={theme}
                            onAccept={this.handleBulkDeleteEntities}
                            onCancel={this.onBulkDeleteDismiss}
                            onDismiss={this.onBulkDeleteDismiss}
                            active
                            innerText={bulkDisableWarningMessage}
                            title={t(
                                'entityManagement:bulkDeleteConfirmationTitle',
                                {
                                    selectedRows: !allFilteredEntitiesSelected
                                        ? selectedRows.length
                                        : totalPageEntities
                                }
                            )}
                        />
                    ) : null}

                    {showCascadeConfirmation ? (
                        <ConfirmationDialog
                            isLoading={isEntityDisableInProgress}
                            theme={theme}
                            onAccept={this.handleDeleteEntityClick}
                            onCancel={this.onCascadeWarningDismiss}
                            onDismiss={this.onCascadeWarningDismiss}
                            active
                            innerText={t(
                                'entityManagement:entityCascadeDeleteConfirmationMessage',
                                {
                                    selectedRows: selectedRows.length
                                }
                            )}
                            title={t(
                                'entityManagement:bulkDeleteConfirmationTitle',
                                {
                                    selectedRows: selectedRows.length
                                }
                            )}
                        />
                    ) : null}

                    {showResetPasswordConfirmation ? (
                        <ConfirmationDialog
                            isLoading={isResetPasswordInProgress}
                            theme={theme}
                            onAccept={this.handleResetPasswordConfirmation}
                            onCancel={this.onResetPasswordDismiss}
                            onDismiss={this.onResetPasswordDismiss}
                            acceptActionLabel={t('yesReset')}
                            active
                            innerText={t(
                                'entityManagement:bulkResetPasswordConfirmationMessage',
                                {
                                    entityName,
                                    selectedRows: selectedRowsCount
                                }
                            )}
                            title={t(
                                'entityManagement:bulkResetPasswordConfirmationTitle',
                                {
                                    entityName,
                                    selectedRows: selectedRowsCount
                                }
                            )}
                            confirmButtonTestId={
                                entityIds.resetPasswordConfirmationButton
                            }
                        />
                    ) : null}

                    {showBulkResetPasswordConfirmation ? (
                        <ConfirmationDialog
                            isLoading={isResetPasswordInProgress}
                            theme={theme}
                            onAccept={this.handleBulkResetPassword}
                            onCancel={this.onBulkResetPasswordDismiss}
                            onDismiss={this.onBulkResetPasswordDismiss}
                            acceptActionLabel={t('yesReset')}
                            active
                            innerText={t(
                                'entityManagement:bulkResetPasswordConfirmationMessage',
                                {
                                    entityName,
                                    selectedRows: selectedRowsCount
                                }
                            )}
                            title={t(
                                'entityManagement:bulkResetPasswordConfirmationTitle',
                                {
                                    entityName,
                                    selectedRows: selectedRowsCount
                                }
                            )}
                        />
                    ) : null}

                    {showBulkResendPasswordConfirmation ? (
                        <ConfirmationDialog
                            isLoading={isResendPasswordInProgress}
                            theme={theme}
                            onAccept={this.handleBulkResendPassword}
                            onDismiss={this.onBulkResendPasswordDismiss}
                            acceptActionLabel={t('yesResend')}
                            active
                            innerText={t(
                                'entityManagement:bulkResendPasswordConfirmationMessage',
                                {
                                    selectedRows: !allFilteredEntitiesSelected
                                        ? selectedRows.length
                                        : totalPageEntities
                                }
                            )}
                            title={t(
                                'entityManagement:bulkResendPasswordConfirmationTitle',
                                {
                                    selectedRows: !allFilteredEntitiesSelected
                                        ? selectedRows.length
                                        : totalPageEntities
                                }
                            )}
                        />
                    ) : null}

                    {showResetQrCodeConfirmation ? (
                        <ConfirmationDialog
                            isLoading={isResetQrCodeInProgress}
                            theme={theme}
                            onAccept={this.handleResetQrCodeConfirmation}
                            onCancel={this.onResetQrCodeDismiss}
                            onDismiss={this.onResetQrCodeDismiss}
                            acceptActionLabel={t('yesReset')}
                            active
                            innerText={this.getQrCodeInnerText()}
                            title={`${t('entityManagement:resetQrCode')} ?`}
                        />
                    ) : null}
                    {showGeneratedQrCode ? (
                        <QrCodeModal
                            isLoading={isResetQrCodeInProgress}
                            onDismiss={this.onGeneratedQrCodeDismiss}
                            active
                            innerText={this.getQrCodeInnerText()}
                            title={t('entityManagement:qrCode_plural')}
                            t={t}
                            qrCodes={qrCodes}
                        />
                    ) : null}
                    {EnableEntityView && isEnableEntityActive ? (
                        <EnableEntityView
                            toggleView={this.toggleEnableEntityView}
                            reloadData={this.reloadData}
                        />
                    ) : null}

                    {isFunction(children)
                        ? children({
                              onDelete: this.handleDeleteEntityClick,
                              reloadData: this.reloadData
                          })
                        : children}
                </div>
            );
        }
    }

    const mapStateToProps = (state, props) => {
        const localeSettings = getLocaleSettings(state, props);
        return {
            localeSettings
        };
    };

    return connect(mapStateToProps)(EntityMasterContainer);
};

export { entityMasterFactory };
export type { Props as MasterContainerProps };
